import React, { useState, useEffect } from 'react';
import _get from 'lodash/get';
import { client } from 'cccisd-apollo';
import Loader from 'cccisd-loader';
import style from './style.css';
import { initialValues } from '../../../Builder';

import metricsByDeploymentQuery from './metricsByDeployment.graphql';
import postMetricsByDeploymentQuery from './postMetricsByDeployment.graphql';

import HtmlRender from './renders/Html.js';
import VariableRender from './renders/Variable.js';
import DragDropRender from './renders/DragDrop.js';

const DynamicWidgetListPlayer = props => {
    let {
        settings: { widgetList, pageNum },
        widgetProps: { widgetMargin, answerBackgroundColor },
    } = props;

    const deploymentId = props.widgetProps?.deploymentId || null;
    const siteName = props.widgetProps?.siteName || null;
    const reportType = props.widgetProps?.reportType || 'pre';
    const preDeploymentId = props.widgetProps?.preDeploymentId || null;

    if (!widgetMargin && widgetMargin !== 0) {
        widgetMargin = initialValues.widgetMargin;
    }

    const [metricsData, setMetricsData] = useState(null);
    const [preMetricsData, setPreMetricsData] = useState(null);
    const [completedResponses, setCompletedResponses] = useState(null);
    const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);

    useEffect(() => {
        async function getMetricsData() {
            setIsLoadingMetrics(true);
            const assignmentIds = [];
            const variableIds = [];
            widgetList.forEach(widget => {
                if (widget.type === 'variable') {
                    if (!assignmentIds.includes(widget.assignmentId)) {
                        assignmentIds.push(widget.assignmentId);
                    }

                    if (!variableIds.includes(widget.variable.variableId)) {
                        variableIds.push(widget.variable.variableId);
                    }
                }

                if (widget.type === 'dragdrop') {
                    if (!assignmentIds.includes(widget.assignmentId)) {
                        assignmentIds.push(widget.assignmentId);
                    }

                    widget.dragDrop.variableIds.forEach(id => {
                        if (!widget.dragDrop.variableIdReportHeaderMapping[id].includes('_duration')) {
                            variableIds.push(id);
                        }
                    });
                }
            });
            let resp;
            if (deploymentId) {
                resp = await client.query({
                    query: reportType === 'pre' ? metricsByDeploymentQuery : postMetricsByDeploymentQuery,
                    variables:
                        reportType === 'pre'
                            ? {
                                  assignmentIds,
                                  deploymentId,
                                  variableIds,
                              }
                            : {
                                  assignmentIds,
                                  deploymentId,
                                  variableIds,
                                  preDeploymentId,
                              },
                    fetchPolicy: 'network-only',
                });
            } else {
                setIsLoadingMetrics(false);
                return;
            }

            const totalCompletedResponses = _get(resp, 'data.flows.assignmentProgressCount', 0);
            setCompletedResponses(totalCompletedResponses);
            const progressSummary = _get(resp, 'data.flows.assignmentProgressSummary', null);
            // if empty, leave metricsData as null — widgets render with blank answers
            setMetricsData(progressSummary ? progressSummary : null);

            const usePreResults = reportType === 'post';
            if (usePreResults) {
                const preProgressSummary = _get(resp, 'data.flows.pre', null);
                setPreMetricsData(preProgressSummary ? preProgressSummary : null);
            }

            setIsLoadingMetrics(false);
        }

        if (deploymentId) {
            getMetricsData();
        }
    }, [widgetList, deploymentId, pageNum, preDeploymentId, reportType]);

    const answerBg = /^#[0-9a-zA-Z]{3,}/.test(answerBackgroundColor)
        ? answerBackgroundColor
        : initialValues.answerBackgroundColor;

    const contentRenderMapper = {
        variable: VariableRender,
        html: HtmlRender,
        dragdrop: DragDropRender,
    };

    const renderWidgets = () => (
        <div>
            <p className={style.reportInfo}>
                Report Date:{' '}
                {new Date().toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                })}
            </p>
            <p className={style.reportInfo}>Site Name: {siteName}</p>
            <p className={style.reportInfo}>Total Survey Submissions: {completedResponses}</p>
            {widgetList.map((widget, i) => {
                const Component = contentRenderMapper[widget.type];
                return (
                    <div
                        key={widget.id}
                        style={{
                            margin: i === widgetList.length - 1 ? '0 0 0 0' : `0 0 ${widgetMargin}px 0`,
                        }}
                    >
                        <Component
                            widget={widget}
                            metricsData={metricsData}
                            preMetricsData={preMetricsData}
                            answerBg={answerBg}
                            reportType={reportType}
                        />
                    </div>
                );
            })}
        </div>
    );

    return isLoadingMetrics ? (
        <div className={style.loader}>
            <Loader loading type="inline" />
        </div>
    ) : (
        renderWidgets()
    );
};

export default DynamicWidgetListPlayer;
